import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Settings, Target, DollarSign, Eye, MousePointer, Users, TrendingUp, Zap, BarChart3, Save, ChevronLeft, ChevronRight, UserCheck, Activity, Loader2, CheckCircle, AlertCircle, Calendar, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { LastEditedBy } from '@/components/shared/LastEditedBy';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Section Header Component
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

// KPI Target Card with Monthly + Daily breakdown
function KPITargetCard({ metric, monthlyValue, dailyValue, onMonthlyChange, onDailyChange, daysInMonth }) {
  const Icon = metric.icon;
  
  // Auto-calculate daily from monthly if daily is not manually set
  const calculatedDaily = monthlyValue > 0 ? Math.round((monthlyValue / daysInMonth) * 100) / 100 : 0;
  const displayDaily = dailyValue > 0 ? dailyValue : calculatedDaily;
  
  return (
    <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg p-4 hover:border-[#E63946]/40 transition-all" data-testid={`kpi-card-${metric.key}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${metric.gradient}`}>
          <Icon size={16} className="text-white" />
        </div>
        <Label className="text-sm font-medium text-white">{metric.label}</Label>
      </div>
      
      {/* Monthly Target */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={12} className="text-[#9CA3AF]" />
            <span className="text-xs text-[#9CA3AF]">Monthly Target</span>
          </div>
          <div className="flex items-center gap-2">
            {metric.prefix && <span className="text-sm text-[#6B7280]">{metric.prefix}</span>}
            <Input
              type="number"
              value={monthlyValue || ''}
              onChange={(e) => onMonthlyChange(metric.key, parseFloat(e.target.value) || 0)}
              placeholder="0"
              className="h-9 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white font-mono text-sm"
              data-testid={`kpi-input-monthly-${metric.key}`}
            />
            {metric.suffix && <span className="text-sm text-[#6B7280]">{metric.suffix}</span>}
          </div>
        </div>
        
        {/* Daily Target */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Target size={12} className="text-[#9CA3AF]" />
              <span className="text-xs text-[#9CA3AF]">Daily Target</span>
            </div>
            {monthlyValue > 0 && dailyValue === 0 && (
              <span className="text-[10px] text-[#6B7280] italic">auto-calculated</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {metric.prefix && <span className="text-sm text-[#6B7280]">{metric.prefix}</span>}
            <Input
              type="number"
              value={dailyValue > 0 ? dailyValue : (calculatedDaily > 0 ? calculatedDaily : '')}
              onChange={(e) => onDailyChange(metric.key, parseFloat(e.target.value) || 0)}
              placeholder={calculatedDaily > 0 ? calculatedDaily.toString() : "0"}
              className="h-9 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white/70 font-mono text-sm"
              data-testid={`kpi-input-daily-${metric.key}`}
            />
            {metric.suffix && <span className="text-sm text-[#6B7280]">{metric.suffix}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Month names
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Simple Target Card for Monthly/Yearly targets
function SimpleTargetCard({ metric, value, onChange }) {
  const Icon = metric.icon;
  
  return (
    <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg p-4 hover:border-[#E63946]/40 transition-all">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${metric.gradient}`}>
          <Icon size={16} className="text-white" />
        </div>
        <Label className="text-sm font-medium text-white">{metric.label}</Label>
      </div>
      <div className="flex items-center gap-2">
        {metric.prefix && <span className="text-sm text-[#6B7280]">{metric.prefix}</span>}
        <Input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(metric.key, parseFloat(e.target.value) || 0)}
          placeholder="0"
          className="h-9 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white font-mono text-sm"
        />
        {metric.suffix && <span className="text-sm text-[#6B7280]">{metric.suffix}</span>}
      </div>
    </div>
  );
}

export function ChannelSettingsContent({ ib }) {
  const [activeTab, setActiveTab] = useState('kpi');
  const [activeKpiTab, setActiveKpiTab] = useState('marketing');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [editMeta, setEditMeta] = useState({});

  // Warn user before navigating away with unsaved changes (prevents "type, refresh, gone")
  React.useEffect(() => {
    if (!hasChanges) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = 'Anda ada perubahan yang belum disimpan. Klik "Save Targets" dulu sebelum tinggalkan page.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges]);
  
  // Channel ID for API
  const channelId = ib?.id || 'default';
  
  // Month key for state
  const monthKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
  
  // Days in current month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  
  // KPI Targets state - stored per month (includes both monthly and daily)
  const [kpiTargets, setKpiTargets] = useState({});
  
  // Monthly Targets state (for Channel Profile Monthly Targets section)
  const [monthlyTargets, setMonthlyTargets] = useState({});
  
  // Yearly Targets state
  const [yearlyTargets, setYearlyTargets] = useState({});
  
  // Get current month's KPI targets with defaults
  const currentTargets = useMemo(() => {
    return kpiTargets[monthKey] || {
      marketing: { 
        monthly: { totalSpend: 0, impressions: 0, linkClicks: 0, lpv: 0, ctaButton: 0, cpl: 0, cpa: 0 },
        daily: { totalSpend: 0, impressions: 0, linkClicks: 0, lpv: 0, ctaButton: 0, cpl: 0, cpa: 0 }
      },
      sales: { 
        monthly: { greeting: 0, oneTick: 0, registration: 0, broker: 0, priceOffer: 0, responseTime: 0, followUpRate: 0 },
        daily: { greeting: 0, oneTick: 0, registration: 0, broker: 0, priceOffer: 0, responseTime: 0, followUpRate: 0 }
      },
      operations: { 
        monthly: { liveSessions: 0, signalsSent: 0, contentPosts: 0, engagement: 0, retention: 0 },
        daily: { liveSessions: 0, signalsSent: 0, contentPosts: 0, engagement: 0, retention: 0 }
      }
    };
  }, [kpiTargets, monthKey]);
  
  // Get current month's Monthly Targets (for profile display)
  const currentMonthlyTargets = useMemo(() => {
    return monthlyTargets[monthKey] || {
      subscribers: 0, leads: 0, closed: 0, deposit: 0, activeClient: 0, tradedLot: 0, commission: 0
    };
  }, [monthlyTargets, monthKey]);
  
  // Get current month's Monthly Missions
  const currentMonthlyMissions = useMemo(() => {
    const saved = monthlyTargets[`${monthKey}_missions`];
    return saved || {
      adsContent: 0, organicContent: 0, webinar: 0, meetup: 0, live: 0, livetrade: 0, signal: 0
    };
  }, [monthlyTargets, monthKey]);
  
  // Get current year's Yearly Targets
  const currentYearlyTargets = useMemo(() => {
    return yearlyTargets[currentYear] || {
      subscribers: 0, leads: 0, closed: 0, deposit: 0, activeClient: 0, tradedLot: 0, commission: 0
    };
  }, [yearlyTargets, currentYear]);
  
  // Get current year's Yearly Missions
  const currentYearlyMissions = useMemo(() => {
    const saved = yearlyTargets[`${currentYear}_missions`];
    return saved || {
      adsContent: 0, organicContent: 0, webinar: 0, meetup: 0, live: 0, livetrade: 0, signal: 0
    };
  }, [yearlyTargets, currentYear]);
  
  // Marketing KPI metrics
  const marketingMetrics = [
    { key: 'totalSpend', label: 'Total Ad Spend', icon: DollarSign, gradient: 'from-[#E63946] to-[#E63946]/50', prefix: 'RM' },
    { key: 'impressions', label: 'Impressions', icon: Eye, gradient: 'from-[#3B82F6] to-[#3B82F6]/50' },
    { key: 'linkClicks', label: 'Link Clicks', icon: MousePointer, gradient: 'from-[#F97316] to-[#F97316]/50' },
    { key: 'lpv', label: 'Landing Page Views', icon: Eye, gradient: 'from-[#10B981] to-[#10B981]/50' },
    { key: 'ctaButton', label: 'CTA Button Clicks', icon: MousePointer, gradient: 'from-[#F59E0B] to-[#F59E0B]/50' },
    { key: 'cpl', label: 'Cost Per Lead', icon: DollarSign, gradient: 'from-[#8B5CF6] to-[#8B5CF6]/50', prefix: 'RM' },
    { key: 'cpa', label: 'Cost Per Acquisition', icon: DollarSign, gradient: 'from-[#EC4899] to-[#EC4899]/50', prefix: 'RM' },
  ];
  
  // Sales KPI metrics
  const salesMetrics = [
    { key: 'greeting', label: 'Greeting Stage', icon: Users, gradient: 'from-[#3B82F6] to-[#3B82F6]/50' },
    { key: 'oneTick', label: 'One Tick Stage', icon: Zap, gradient: 'from-[#10B981] to-[#10B981]/50' },
    { key: 'registration', label: 'Registration Stage', icon: UserCheck, gradient: 'from-[#F59E0B] to-[#F59E0B]/50' },
    { key: 'broker', label: 'Broker Selection', icon: Target, gradient: 'from-[#8B5CF6] to-[#8B5CF6]/50' },
    { key: 'priceOffer', label: 'Price Offer Stage', icon: DollarSign, gradient: 'from-[#E63946] to-[#E63946]/50' },
    { key: 'responseTime', label: 'Target Response Time', icon: Activity, gradient: 'from-[#F97316] to-[#F97316]/50', suffix: 'hrs' },
    { key: 'followUpRate', label: 'Follow-up Rate', icon: TrendingUp, gradient: 'from-[#EC4899] to-[#EC4899]/50', suffix: '%' },
  ];
  
  // Operations KPI metrics
  const operationsMetrics = [
    { key: 'liveSessions', label: 'Live Sessions', icon: Activity, gradient: 'from-[#F97316] to-[#F97316]/50' },
    { key: 'signalsSent', label: 'Signals to Send', icon: TrendingUp, gradient: 'from-[#8B5CF6] to-[#8B5CF6]/50' },
    { key: 'contentPosts', label: 'Content Posts', icon: Eye, gradient: 'from-[#3B82F6] to-[#3B82F6]/50' },
    { key: 'engagement', label: 'Engagement Rate', icon: Users, gradient: 'from-[#10B981] to-[#10B981]/50', suffix: '%' },
    { key: 'retention', label: 'Retention Rate', icon: UserCheck, gradient: 'from-[#F59E0B] to-[#F59E0B]/50', suffix: '%' },
  ];
  
  // Monthly/Yearly Target metrics (for Profile display)
  const profileTargetMetrics = [
    { key: 'subscribers', label: 'Subscribers', icon: Users, gradient: 'from-[#3B82F6] to-[#3B82F6]/50' },
    { key: 'leads', label: 'Leads', icon: Target, gradient: 'from-[#10B981] to-[#10B981]/50' },
    { key: 'closed', label: 'Closed', icon: Zap, gradient: 'from-[#F59E0B] to-[#F59E0B]/50' },
    { key: 'deposit', label: 'Deposit', icon: DollarSign, gradient: 'from-[#E63946] to-[#E63946]/50', prefix: '$' },
    { key: 'activeClient', label: 'Active Client', icon: UserCheck, gradient: 'from-[#8B5CF6] to-[#8B5CF6]/50' },
    { key: 'tradedLot', label: 'Traded Lot', icon: BarChart3, gradient: 'from-[#F97316] to-[#F97316]/50' },
    { key: 'commission', label: 'Commission', icon: TrendingUp, gradient: 'from-[#EC4899] to-[#EC4899]/50', prefix: 'RM' },
  ];
  
  // Mission metrics (activities to achieve targets)
  const missionMetrics = [
    { key: 'adsContent', label: 'Ads Content', icon: Target, gradient: 'from-[#E63946] to-[#E63946]/50', description: 'Paid advertising content pieces' },
    { key: 'organicContent', label: 'Organic Content', icon: Eye, gradient: 'from-[#10B981] to-[#10B981]/50', description: 'Social media & organic posts' },
    { key: 'webinar', label: 'Webinar', icon: Users, gradient: 'from-[#3B82F6] to-[#3B82F6]/50', description: 'Online educational sessions' },
    { key: 'meetup', label: 'Meetup', icon: Users, gradient: 'from-[#8B5CF6] to-[#8B5CF6]/50', description: 'Physical meetup events' },
    { key: 'live', label: 'Live', icon: Activity, gradient: 'from-[#EC4899] to-[#EC4899]/50', description: 'Live streaming sessions' },
    { key: 'livetrade', label: 'Live Trade', icon: TrendingUp, gradient: 'from-[#F59E0B] to-[#F59E0B]/50', description: 'Live trading demonstrations' },
    { key: 'signal', label: 'Signal', icon: Zap, gradient: 'from-[#06B6D4] to-[#06B6D4]/50', description: 'Trading signals shared' },
  ];
  
  // Fetch targets from API
  const fetchTargets = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch KPI Targets
      const kpiResponse = await fetch(`${API_URL}/api/kpi-targets/${channelId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (kpiResponse.ok) {
        const data = await kpiResponse.json();
        // Transform old format to new format with monthly/daily
        const transformed = {};
        Object.entries(data.targets || {}).forEach(([key, value]) => {
          transformed[key] = {
            marketing: { 
              monthly: value.marketing || {}, 
              daily: value.marketingDaily || {} 
            },
            sales: { 
              monthly: value.sales || {}, 
              daily: value.salesDaily || {} 
            },
            operations: { 
              monthly: value.operations || {}, 
              daily: value.operationsDaily || {} 
            }
          };
        });
        setKpiTargets(transformed);
      }
      
      // Fetch Channel Targets (Monthly & Yearly) from unified endpoint
      const channelTargetsResponse = await fetch(`${API_URL}/api/channel-targets/${channelId}/${currentYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (channelTargetsResponse.ok) {
        const data = await channelTargetsResponse.json();
        
        // Merge monthly targets with monthly missions
        const mergedMonthly = { ...(data.monthly || {}) };
        if (data.monthly_missions) {
          Object.entries(data.monthly_missions).forEach(([monthKey, missions]) => {
            mergedMonthly[`${monthKey}_missions`] = missions;
          });
        }
        setMonthlyTargets(mergedMonthly);
        
        // Merge yearly targets with yearly missions
        const mergedYearly = { [currentYear]: data.yearly || {} };
        if (data.yearly_missions) {
          mergedYearly[`${currentYear}_missions`] = data.yearly_missions;
        }
        setYearlyTargets(mergedYearly);
        
        // If primary source returned empty yearly, try ib-data backup
        if (!data.yearly || Object.keys(data.yearly).length === 0) {
          try {
            const token2 = localStorage.getItem('token');
            const headers2 = { Authorization: `Bearer ${token2}` };
            const [monthlyRes, yearlyRes] = await Promise.all([
              axios.get(`${API}/ib-data/${channelId}/monthly_targets`, { headers: headers2 }).catch(() => ({ data: { data: null } })),
              axios.get(`${API}/ib-data/${channelId}/yearly_targets`, { headers: headers2 }).catch(() => ({ data: { data: null } })),
            ]);
            if (yearlyRes.data?.data && Object.keys(yearlyRes.data.data).length > 0) {
              setYearlyTargets(yearlyRes.data.data);
            }
            if (monthlyRes.data?.data && Object.keys(monthlyRes.data.data).length > 0) {
              setMonthlyTargets(prev => ({ ...monthlyRes.data.data, ...prev }));
            }
          } catch {}
        }
      } else {
        // Fallback to MongoDB generic store
        try {
          const token = localStorage.getItem('token');
          const headers = { Authorization: `Bearer ${token}` };
          const [kpiRes, monthlyRes, yearlyRes] = await Promise.all([
            axios.get(`${API}/ib-data/${channelId}/kpi_targets`, { headers }).catch(() => ({ data: { data: null } })),
            axios.get(`${API}/ib-data/${channelId}/monthly_targets`, { headers }).catch(() => ({ data: { data: null } })),
            axios.get(`${API}/ib-data/${channelId}/yearly_targets`, { headers }).catch(() => ({ data: { data: null } })),
          ]);
          if (kpiRes.data?.data) setKpiTargets(kpiRes.data.data);
          if (monthlyRes.data?.data) setMonthlyTargets(monthlyRes.data.data);
          if (yearlyRes.data?.data) setYearlyTargets(yearlyRes.data.data);
          // Get latest edit metadata
          const latestMeta = [kpiRes.data, monthlyRes.data, yearlyRes.data]
            .filter(d => d?.updated_at)
            .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
          if (latestMeta?.updated_by_name) {
            setEditMeta({ name: latestMeta.updated_by_name, time: latestMeta.updated_at });
          }
        } catch {}
      }
      
    } catch (error) {
      console.error('Error fetching targets:', error);
      // Fallback to MongoDB generic store
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [kpiRes, monthlyRes, yearlyRes] = await Promise.all([
          axios.get(`${API}/ib-data/${channelId}/kpi_targets`, { headers }).catch(() => ({ data: { data: null } })),
          axios.get(`${API}/ib-data/${channelId}/monthly_targets`, { headers }).catch(() => ({ data: { data: null } })),
          axios.get(`${API}/ib-data/${channelId}/yearly_targets`, { headers }).catch(() => ({ data: { data: null } })),
        ]);
        if (kpiRes.data?.data) setKpiTargets(kpiRes.data.data);
        if (monthlyRes.data?.data) setMonthlyTargets(monthlyRes.data.data);
        if (yearlyRes.data?.data) setYearlyTargets(yearlyRes.data.data);
        const latestMeta2 = [kpiRes.data, monthlyRes.data, yearlyRes.data]
          .filter(d => d?.updated_at)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
        if (latestMeta2?.updated_by_name) {
          setEditMeta({ name: latestMeta2.updated_by_name, time: latestMeta2.updated_at });
        }
      } catch {}
    } finally {
      setIsLoading(false);
    }
  }, [channelId, currentYear]);
  
  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);
  
  // Update monthly target for current month
  const handleMonthlyChange = (department, key, value) => {
    setKpiTargets(prev => ({
      ...prev,
      [monthKey]: {
        ...prev[monthKey],
        [department]: {
          ...(prev[monthKey]?.[department] || {}),
          monthly: {
            ...(prev[monthKey]?.[department]?.monthly || {}),
            [key]: value
          }
        }
      }
    }));
    setHasChanges(true);
    setSaveStatus(null);
  };
  
  // Update daily target for current month
  const handleDailyChange = (department, key, value) => {
    setKpiTargets(prev => ({
      ...prev,
      [monthKey]: {
        ...prev[monthKey],
        [department]: {
          ...(prev[monthKey]?.[department] || {}),
          daily: {
            ...(prev[monthKey]?.[department]?.daily || {}),
            [key]: value
          }
        }
      }
    }));
    setHasChanges(true);
    setSaveStatus(null);
  };
  
  // Update Yearly Target — auto divide to all 12 months (defensive — works even if existing monthly state has weird shape)
  const handleYearlyTargetChange = (key, value) => {
    const numVal = parseFloat(value) || 0;
    const perMonth = Math.round(numVal / 12);
    
    // Update yearly
    setYearlyTargets(prev => ({
      ...prev,
      [currentYear]: {
        ...(prev[currentYear] || {}),
        [key]: numVal
      }
    }));
    
    // Auto-fill all 12 months with yearly/12 (force-set even if existing was stored at top-level
    // with nested/legacy structure — we always write the FLAT key here so UI reads it correctly)
    setMonthlyTargets(prev => {
      const updated = { ...prev };
      for (let m = 1; m <= 12; m++) {
        const mk = `${currentYear}-${String(m).padStart(2, '0')}`;
        const existing = updated[mk];
        // Defensive: if existing is not a plain object (e.g., legacy nested structure), reset to {}
        const base = (existing && typeof existing === 'object' && !Array.isArray(existing)) ? existing : {};
        updated[mk] = { ...base, [key]: perMonth };
      }
      return updated;
    });
    
    setHasChanges(true);
    setSaveStatus(null);
  };
  
  // Update Yearly Mission — auto divide to all 12 months
  const handleYearlyMissionChange = (key, value) => {
    const numVal = parseFloat(value) || 0;
    const perMonth = Math.round(numVal / 12);
    
    setYearlyTargets(prev => ({
      ...prev,
      [`${currentYear}_missions`]: {
        ...(prev[`${currentYear}_missions`] || {}),
        [key]: numVal
      }
    }));
    
    // Auto-fill all 12 months missions
    setMonthlyTargets(prev => {
      const updated = { ...prev };
      for (let m = 1; m <= 12; m++) {
        const mk = `${currentYear}-${String(m).padStart(2, '0')}_missions`;
        updated[mk] = { ...(updated[mk] || {}), [key]: perMonth };
      }
      return updated;
    });
    
    setHasChanges(true);
    setSaveStatus(null);
  };
  
  // Update Monthly Target — recalculate yearly total from all 12 months
  const handleMonthlyTargetChange = (key, value) => {
    const numVal = parseFloat(value) || 0;
    
    setMonthlyTargets(prev => {
      const updated = {
        ...prev,
        [monthKey]: { ...(prev[monthKey] || {}), [key]: numVal }
      };
      
      // Recalculate yearly total from all 12 months
      let yearlyTotal = 0;
      for (let m = 1; m <= 12; m++) {
        const mk = `${currentYear}-${String(m).padStart(2, '0')}`;
        yearlyTotal += parseFloat(updated[mk]?.[key]) || 0;
      }
      
      // Update yearly target to reflect sum
      setYearlyTargets(yp => ({
        ...yp,
        [currentYear]: { ...(yp[currentYear] || {}), [key]: yearlyTotal }
      }));
      
      return updated;
    });
    
    setHasChanges(true);
    setSaveStatus(null);
  };
  
  // Update Monthly Mission — recalculate yearly total
  const handleMonthlyMissionChange = (key, value) => {
    const numVal = parseFloat(value) || 0;
    
    setMonthlyTargets(prev => {
      const updated = {
        ...prev,
        [`${monthKey}_missions`]: { ...(prev[`${monthKey}_missions`] || {}), [key]: numVal }
      };
      
      // Recalculate yearly mission total
      let yearlyTotal = 0;
      for (let m = 1; m <= 12; m++) {
        const mk = `${currentYear}-${String(m).padStart(2, '0')}_missions`;
        yearlyTotal += parseFloat(updated[mk]?.[key]) || 0;
      }
      
      setYearlyTargets(yp => ({
        ...yp,
        [`${currentYear}_missions`]: { ...(yp[`${currentYear}_missions`] || {}), [key]: yearlyTotal }
      }));
      
      return updated;
    });
    
    setHasChanges(true);
    setSaveStatus(null);
  };
  
  // Save to API
  const saveTargets = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      const token = localStorage.getItem('token');
      const departments = ['marketing', 'sales', 'operations'];
      
      // Save each department's KPI targets (both monthly and daily)
      for (const dept of departments) {
        const monthlyTargets = currentTargets[dept]?.monthly || {};
        const dailyTargets = currentTargets[dept]?.daily || {};
        
        // Save monthly targets
        await fetch(`${API_URL}/api/kpi-targets`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            channel_id: channelId,
            year: currentYear,
            month: currentMonth + 1,
            department: dept,
            targets: monthlyTargets
          })
        });
        
        // Save daily targets as separate entry
        await fetch(`${API_URL}/api/kpi-targets`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            channel_id: channelId,
            year: currentYear,
            month: currentMonth + 1,
            department: `${dept}Daily`,
            targets: dailyTargets
          })
        });
      }
      
      // Save Monthly Targets for ALL 12 months (auto-divided from yearly)
      for (let m = 1; m <= 12; m++) {
        const mk = `${currentYear}-${String(m).padStart(2, '0')}`;
        const monthTargets = monthlyTargets[mk] || currentMonthlyTargets;
        const monthMissions = monthlyTargets[`${mk}_missions`] || currentMonthlyMissions;
        
        await fetch(`${API_URL}/api/channel-targets`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            channel_id: channelId, year: currentYear, month: m,
            type: 'monthly', targets: monthTargets
          })
        });
        
        await fetch(`${API_URL}/api/channel-targets`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            channel_id: channelId, year: currentYear, month: m,
            type: 'monthly_missions', targets: monthMissions
          })
        });
      }
      
      // Save Yearly Targets
      await fetch(`${API_URL}/api/channel-targets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel_id: channelId,
          year: currentYear,
          type: 'yearly',
          targets: currentYearlyTargets
        })
      });
      
      // Save Yearly Missions
      await fetch(`${API_URL}/api/channel-targets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel_id: channelId,
          year: currentYear,
          type: 'yearly_missions',
          targets: currentYearlyMissions
        })
      });
      
      // Save to MongoDB as backup
      const mongoHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      await Promise.all([
        axios.put(`${API}/ib-data/${channelId}/kpi_targets`, { data: kpiTargets }, { headers: mongoHeaders }).catch((e) => console.error("Target save failed:", e)),
        axios.put(`${API}/ib-data/${channelId}/monthly_targets`, { data: { ...monthlyTargets, [`${monthKey}_missions`]: currentMonthlyMissions } }, { headers: mongoHeaders }).catch((e) => console.error("Target save failed:", e)),
        axios.put(`${API}/ib-data/${channelId}/yearly_targets`, { data: { ...yearlyTargets, [`${currentYear}_missions`]: currentYearlyMissions } }, { headers: mongoHeaders }).catch((e) => console.error("Target save failed:", e)),
      ]);
      
      // Dispatch event to notify Channel Profile to refresh
      window.dispatchEvent(new CustomEvent('channelTargetsUpdated', {
        detail: { channelId, type: 'all' }
      }));
      
      setSaveStatus('success');
      setHasChanges(false);
      
      // Log edit history
      try {
        await fetch(`${API_URL}/api/edit-history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            resource_type: 'channel-settings',
            resource_id: channelId,
            page: 'Channel Settings',
            section: 'Targets & Missions',
            action: 'UPDATE',
            changes: [
              { field: 'KPI Targets', detail: 'Updated marketing/sales/operation KPI' },
              { field: 'Monthly Missions', detail: 'Updated monthly activity targets' },
              { field: 'Yearly Targets', detail: 'Updated yearly performance targets' },
            ]
          })
        });
      } catch {}
      
      toast.success('All Targets & Missions Saved!', {
        description: `KPI, Monthly, Yearly targets and missions saved successfully.`
      });
    } catch (error) {
      console.error('Error saving targets:', error);
      setSaveStatus('error');
      toast.error('Failed to save', {
        description: 'Please try again.'
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };
  
  // Month navigation
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  // Copy from previous month
  const copyFromPreviousMonth = async () => {
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const prevMonthKey = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}`;

    let copiedAnything = false;

    // 1) Copy KPI targets (kpiTargets) if previous month has data
    if (kpiTargets[prevMonthKey] && Object.keys(kpiTargets[prevMonthKey]).length > 0) {
      setKpiTargets(prev => ({
        ...prev,
        [monthKey]: JSON.parse(JSON.stringify(prev[prevMonthKey]))
      }));
      copiedAnything = true;
    }

    // 2) Copy Monthly targets (monthlyTargets) if previous month has data
    if (monthlyTargets[prevMonthKey] && Object.keys(monthlyTargets[prevMonthKey]).length > 0) {
      setMonthlyTargets(prev => ({
        ...prev,
        [monthKey]: JSON.parse(JSON.stringify(prev[prevMonthKey] || {})),
        [`${monthKey}_missions`]: JSON.parse(JSON.stringify(prev[`${prevMonthKey}_missions`] || {}))
      }));
      copiedAnything = true;
    }

    if (copiedAnything) {
      setHasChanges(true);
      toast.success(`Copied from ${shortMonthNames[prevMonth]} ${prevYear}`, {
        description: 'Click "Save" to persist changes.'
      });
      return;
    }

    // 3) Fallback: Fetch from API
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/kpi-targets/${channelId}/${prevYear}/${prevMonth + 1}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        const hasData = Object.values(data).some(dept => {
          if (!dept || typeof dept !== 'object') return false;
          if (dept.monthly && Object.keys(dept.monthly).length > 0) return true;
          return Object.keys(dept).length > 0;
        });

        if (hasData) {
          const normalized = {};
          for (const [dept, val] of Object.entries(data)) {
            if (val && typeof val === 'object') {
              if (val.monthly || val.daily) {
                normalized[dept] = { monthly: val.monthly || val, daily: val.daily || {} };
              } else {
                normalized[dept] = { monthly: val, daily: {} };
              }
            }
          }

          setKpiTargets(prev => ({
            ...prev,
            [monthKey]: Object.keys(normalized).length > 0 ? normalized : {
              marketing: { monthly: data.marketing || {}, daily: data.marketingDaily || {} },
              sales: { monthly: data.sales || {}, daily: data.salesDaily || {} },
              operations: { monthly: data.operations || {}, daily: data.operationsDaily || {} }
            }
          }));
          setHasChanges(true);
          toast.success(`Copied from ${shortMonthNames[prevMonth]} ${prevYear}`, {
            description: 'Click "Save" to persist changes.'
          });
          return;
        }
      }
    } catch (error) {
      console.error('Error copying targets:', error);
    }

    toast.error('No targets found', {
      description: `${shortMonthNames[prevMonth]} ${prevYear} has no saved targets.`
    });
  };

  // Copy to all remaining months (KPI + Monthly Targets + Missions)
  const copyToAllMonths = async () => {
    const confirmed = window.confirm(`Copy ${monthNames[currentMonth]} targets to all remaining months of ${currentYear}?\n\nThis copies KPI targets, Monthly Targets, and Monthly Missions.`);
    if (!confirmed) return;

    // 1) Copy KPI targets
    setKpiTargets(prev => {
      const updated = { ...prev };
      for (let m = currentMonth + 1; m < 12; m++) {
        const key = `${currentYear}-${String(m + 1).padStart(2, '0')}`;
        updated[key] = JSON.parse(JSON.stringify(currentTargets));
      }
      return updated;
    });

    // 2) Copy Monthly targets + Missions
    setMonthlyTargets(prev => {
      const updated = { ...prev };
      const srcTargets = prev[monthKey] || currentMonthlyTargets;
      const srcMissions = prev[`${monthKey}_missions`] || currentMonthlyMissions;
      for (let m = currentMonth + 1; m < 12; m++) {
        const mk = `${currentYear}-${String(m + 1).padStart(2, '0')}`;
        updated[mk] = JSON.parse(JSON.stringify(srcTargets));
        updated[`${mk}_missions`] = JSON.parse(JSON.stringify(srcMissions));
      }
      return updated;
    });

    setHasChanges(true);
    toast.success('Targets copied to all remaining months', {
      description: 'Click "Save" after reviewing each month.'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#E63946]" />
        <span className="ml-3 text-white">Loading KPI Targets...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="channel-settings-page">
      {/* Fixed Header */}
      <header className="flex-shrink-0 px-8 py-6 border-b border-[hsl(var(--border))] bg-[#0A0A0B]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E63946] to-[#F97316] flex items-center justify-center shadow-lg shadow-[#E63946]/20">
              <Settings size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Channel Settings</h1>
              <p className="text-sm text-[#9CA3AF]">Configure KPI targets for <span className="text-white font-medium">{ib?.name || 'Channel'}</span></p>
              <LastEditedBy name={editMeta.name} timestamp={editMeta.time} resourceType="channel-settings" resourceId={ib?.id} pageName="Channel Settings" />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Month Navigation */}
            <div className="flex items-center gap-1 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg px-2 py-1">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-white hover:bg-white/10" onClick={goToPreviousMonth} data-testid="prev-month-btn">
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm font-semibold text-white min-w-[120px] text-center" data-testid="current-month-display">
                {monthNames[currentMonth]} {currentYear}
              </span>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-white hover:bg-white/10" onClick={goToNextMonth} data-testid="next-month-btn">
                <ChevronRight size={16} />
              </Button>
            </div>
            
            {/* Copy Actions */}
            <Button variant="outline" size="sm" onClick={copyFromPreviousMonth} className="border-[hsl(var(--border))] text-white hover:bg-white/5" data-testid="copy-prev-month-btn">
              <Copy size={14} className="mr-2" />
              Copy Previous
            </Button>
            
            <Button variant="outline" size="sm" onClick={copyToAllMonths} className="border-[hsl(var(--border))] text-white hover:bg-white/5" data-testid="copy-all-months-btn">
              <Copy size={14} className="mr-2" />
              Copy to All
            </Button>
            
            {/* Save Button */}
            <Button 
              onClick={saveTargets} 
              disabled={isSaving || !hasChanges} 
              className={`min-w-[140px] transition-all ${
                saveStatus === 'success' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : saveStatus === 'error'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-gradient-to-r from-[#E63946] to-[#F97316]'
              } text-white`}
              data-testid="save-targets-btn"
            >
            {isSaving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : saveStatus === 'success' ? (
              <>
                <CheckCircle size={16} className="mr-2" />
                Saved!
              </>
            ) : saveStatus === 'error' ? (
              <>
                <AlertCircle size={16} className="mr-2" />
                Retry
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                {hasChanges ? 'Save Changes' : 'Save Targets'}
              </>
            )}
          </Button>
        </div>
      </div>
      </header>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 space-y-6">
      
      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="px-4 py-2 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-400 text-sm">
          ⚠️ You have unsaved changes. Click &quot;Save&quot; to persist.
        </div>
      )}
      
      {/* Days in Month Info */}
      <div className="flex items-center gap-2 text-sm text-[#9CA3AF]">
        <Calendar size={14} />
        <span>{monthNames[currentMonth]} {currentYear} has <span className="text-white font-medium">{daysInMonth} days</span>. Daily targets are auto-calculated from monthly targets.</span>
      </div>
      
      {/* Main Tabs - KPI, Monthly, Yearly */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 w-full max-w-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
          <TabsTrigger value="kpi" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E63946] data-[state=active]:to-[#F97316] data-[state=active]:text-white" data-testid="kpi-main-tab">
            KPI Targets
          </TabsTrigger>
          <TabsTrigger value="monthly" className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white" data-testid="monthly-main-tab">
            Monthly Targets
          </TabsTrigger>
          <TabsTrigger value="yearly" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white" data-testid="yearly-main-tab">
            Yearly Targets
          </TabsTrigger>
        </TabsList>
        
        {/* KPI Targets Tab Content */}
        <TabsContent value="kpi" className="mt-6">
          {/* KPI Sub-Tabs */}
          <Tabs value={activeKpiTab} onValueChange={setActiveKpiTab} className="w-full">
            <TabsList className="grid grid-cols-3 w-full max-w-md bg-[hsl(var(--surface))] border border-[hsl(var(--border))] mb-6">
              <TabsTrigger value="marketing" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white" data-testid="marketing-tab">
                Marketing
              </TabsTrigger>
              <TabsTrigger value="sales" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white" data-testid="sales-tab">
                Sales
              </TabsTrigger>
              <TabsTrigger value="operations" className="data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white" data-testid="operations-tab">
                Operations
              </TabsTrigger>
            </TabsList>
            
            {/* Marketing KPI Targets */}
            <TabsContent value="marketing">
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={Target} title="Marketing KPI Targets" subtitle={`Monthly & Daily targets for ${monthNames[currentMonth]} ${currentYear}`} />
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {marketingMetrics.map((metric) => (
                      <KPITargetCard
                        key={metric.key}
                        metric={metric}
                        monthlyValue={currentTargets.marketing?.monthly?.[metric.key] || 0}
                        dailyValue={currentTargets.marketing?.daily?.[metric.key] || 0}
                        onMonthlyChange={(key, val) => handleMonthlyChange('marketing', key, val)}
                        onDailyChange={(key, val) => handleDailyChange('marketing', key, val)}
                        daysInMonth={daysInMonth}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Sales KPI Targets */}
            <TabsContent value="sales">
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={Zap} title="Sales KPI Targets" subtitle={`Monthly & Daily targets for ${monthNames[currentMonth]} ${currentYear}`} />
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {salesMetrics.map((metric) => (
                      <KPITargetCard
                        key={metric.key}
                        metric={metric}
                        monthlyValue={currentTargets.sales?.monthly?.[metric.key] || 0}
                        dailyValue={currentTargets.sales?.daily?.[metric.key] || 0}
                        onMonthlyChange={(key, val) => handleMonthlyChange('sales', key, val)}
                        onDailyChange={(key, val) => handleDailyChange('sales', key, val)}
                        daysInMonth={daysInMonth}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Operations KPI Targets */}
            <TabsContent value="operations">
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader icon={BarChart3} title="Operations KPI Targets" subtitle={`Monthly & Daily targets for ${monthNames[currentMonth]} ${currentYear}`} />
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {operationsMetrics.map((metric) => (
                      <KPITargetCard
                        key={metric.key}
                        metric={metric}
                        monthlyValue={currentTargets.operations?.monthly?.[metric.key] || 0}
                        dailyValue={currentTargets.operations?.daily?.[metric.key] || 0}
                        onMonthlyChange={(key, val) => handleMonthlyChange('operations', key, val)}
                        onDailyChange={(key, val) => handleDailyChange('operations', key, val)}
                        daysInMonth={daysInMonth}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
        
        {/* Monthly Targets Tab Content */}
        <TabsContent value="monthly" className="mt-6">
          <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <SectionHeader 
              icon={Calendar} 
              title="Monthly Targets" 
              subtitle={`Set channel targets for ${monthNames[currentMonth]} ${currentYear} - displayed in Channel Profile`} 
            />
            <div className="p-6">
              <p className="text-sm text-[#9CA3AF] mb-6">
                Monthly targets for {monthNames[currentMonth]} {currentYear}. <span className="text-[#3B82F6] font-medium">Auto-filled dari Yearly Target ÷ 12</span>. Boleh adjust bulan tertentu — yearly akan auto-update.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {profileTargetMetrics.map((metric) => (
                  <SimpleTargetCard
                    key={metric.key}
                    metric={metric}
                    value={currentMonthlyTargets[metric.key] || 0}
                    onChange={handleMonthlyTargetChange}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Monthly Missions Section */}
          <div className="mt-6 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <SectionHeader 
              icon={Activity} 
              title="Monthly Missions" 
              subtitle={`Activities to achieve targets for ${monthNames[currentMonth]} ${currentYear}`} 
            />
            <div className="p-6">
              <p className="text-sm text-[#9CA3AF] mb-6">
                Set the number of activities you plan to execute this month. These missions help track your operational efforts towards achieving targets.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {missionMetrics.map((metric) => (
                  <SimpleTargetCard
                    key={metric.key}
                    metric={metric}
                    value={currentMonthlyMissions[metric.key] || 0}
                    onChange={handleMonthlyMissionChange}
                  />
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Yearly Targets Tab Content */}
        <TabsContent value="yearly" className="mt-6">
          <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <SectionHeader 
              icon={TrendingUp} 
              title="Yearly Targets" 
              subtitle={`Set channel targets for year ${currentYear} - displayed in Channel Profile`} 
            />
            <div className="p-6">
              <p className="text-sm text-[#9CA3AF] mb-6">
                These are your annual targets displayed in the Channel Profile. <span className="text-[#10B981] font-medium">Auto-divide ke 12 bulan</span> — isi yearly, monthly auto terkira. Jika adjust bulan tertentu, yearly akan auto-update.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {profileTargetMetrics.map((metric) => (
                  <SimpleTargetCard
                    key={metric.key}
                    metric={metric}
                    value={currentYearlyTargets[metric.key] || 0}
                    onChange={handleYearlyTargetChange}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Yearly Missions Section */}
          <div className="mt-6 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <SectionHeader 
              icon={Activity} 
              title="Yearly Missions" 
              subtitle={`Annual activity targets for ${currentYear}`} 
            />
            <div className="p-6">
              <p className="text-sm text-[#9CA3AF] mb-6">
                Set your annual activity targets. These represent the total activities planned for the entire year.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {missionMetrics.map((metric) => (
                  <SimpleTargetCard
                    key={metric.key}
                    metric={metric}
                    value={currentYearlyMissions[metric.key] || 0}
                    onChange={handleYearlyMissionChange}
                  />
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Info Note */}
      <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg p-4">
        <p className="text-sm text-[#9CA3AF]">
          <span className="text-white font-medium">💡 Tip:</span> These KPI targets are used in the Performance Tracker to calculate variance, surplus, and shortfall. 
          Daily targets are auto-calculated from monthly targets but can be overridden manually for more precise daily tracking.
        </p>
      </div>
        </div>
      </div>
    </div>
  );
}
